import { createApi } from '@reduxjs/toolkit/query/react'

import {
  competitionSlice,
  readCompetitionRanking,
} from '@/redux/competition'
import { baseQuery } from '@/redux/utils'

export interface BettingOverview {
  stage: number
  bettingPoints: number
  bettingUsers: number
  updatedAt: number
}

export interface UserCompetitionData {
  balance: number
  mdd: number
  pnl: number
  roi: number
  sharpeRatio: number
  sortinoRatio: number
  rank: number
  updatedAt: number
}

export interface BettingUserOverview {
  totalPoints: number
  bettingPoints: number
  pnl: number
  updatedAt: number
  isCompetitor: boolean
  userCompetitionData: UserCompetitionData | null
}

export interface BettingRecord {
  competitorID: number
  nickname: string
  pointsBet: number
  pnl: number
  liveRedeem: number
  updatedAt: number
}

export interface BettingRecordHistory {
  index: number
  action: number
  amount: number
  createdAt: number
}

export interface BettingRecordHistoryResponse {
  rows: BettingRecordHistory[]
  cursor: number
}

export interface BettingRecordHistoryQueryParams {
  competitionId: number
  competitorId: number
  cursor?: number
  limit?: number
}

export interface BetRequest {
  competitionId: number
  competitorId: number
  amount: number
}

export interface RedeemRequest {
  competitionId: number
  competitorId: number
  amount: number
  myBet: number
  maxRedeem: number
  userInput: number
}

export const readBettingUserOverview = 'readBettingUserOverview'
export const readBettingRecords = 'readBettingRecords'

export const competitionBettingSlice = createApi({
  reducerPath: 'competition-betting',
  baseQuery,
  tagTypes: [readBettingUserOverview, readBettingRecords],
  endpoints: (builder) => ({
    readBettingOverview: builder.query<BettingOverview, number>({
      query: (competitionId) => `/competition/${competitionId}/betting/overview`,
    }),
    readBettingUserOverview: builder.query<BettingUserOverview, number>({
      query: (competitionId) => `/competition/${competitionId}/betting/user/overview`,
      providesTags: [readBettingUserOverview],
    }),
    readBettingRecords: builder.query<BettingRecord[], number>({
      query: (competitionId) => `/competition/${competitionId}/betting/user/betting-records`,
      providesTags: [readBettingRecords],
    }),
    readBettingRecordHistory: builder.query<BettingRecordHistoryResponse, BettingRecordHistoryQueryParams>({
      query: ({
        competitionId,
        competitorId,
        cursor,
        limit,
      }) => `/competition/${competitionId}/betting/user/operation-history?competitorId=${competitorId}&cursor=${cursor ?? ''}&limit=${limit ?? 500}`,
    }),
    competitionBet: builder.mutation<void, BetRequest>({
      query: ({ competitionId, competitorId, amount }) => ({
        url: `/competition/${competitionId}/betting/bet`,
        body: {
          competitorId,
          amount,
        },
        method: 'POST',
      }),
      invalidatesTags: [readBettingUserOverview, readBettingRecords],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(competitionSlice.util.invalidateTags([readCompetitionRanking]))
      },
    }),
    competitionRedeem: builder.mutation<void, RedeemRequest>({
      query: ({ competitionId, competitorId, amount, myBet, maxRedeem, userInput }) => ({
        url: `/competition/${competitionId}/betting/redeem`,
        body: {
          competitorId,
          amount,
          myBet,
          maxRedeem,
          userInput,
        },
        method: 'POST',
      }),
      invalidatesTags: [readBettingUserOverview, readBettingRecords],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(competitionSlice.util.invalidateTags([readCompetitionRanking]))
      },
    }),
  }),
})

export const {
  useReadBettingOverviewQuery,
  useReadBettingUserOverviewQuery,
  useReadBettingRecordsQuery,
  useReadBettingRecordHistoryQuery,
  useCompetitionBetMutation,
  useCompetitionRedeemMutation,
} = competitionBettingSlice
